/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useId, useRef, useState, type ReactNode } from "react";
import { useFocusRing, useTextField } from "react-aria";

import { useFormat } from "@superweb/intl";

import { useMessage } from "#intl";

import { useUiColors } from "../theme";
import { ClearButton } from "./buttons";
import { Field, useTextInputStyle } from "./field";
import { FieldLabel } from "./label";
import { FieldErrorMessage } from "./error-message";
import { FieldDescription } from "./description";
import { FieldCharacterCounter } from "./character-counter";

type InvalidRules = {
  required: boolean;
  maxLength: boolean;
};

export type BaseTextFieldState = {
  value: string;
  errorVisible?: boolean;
  errorMessage?: string;
  invalid?: boolean;
};

export const BaseTextField = ({
  label,
  description,
  ariaDescribedBy,
  type,
  placeholder,
  required = false,
  disabled = false,
  state,
  icon,
  autoFocus = false,
  toolbar,
  inputMode,
  maxLength,
  onChange,
  validate,
}: {
  label: string;
  description?: string;
  ariaDescribedBy?: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  state: BaseTextFieldState;
  icon?: ReactNode;
  autoFocus?: boolean;
  toolbar?: ReactNode;
  inputMode?:
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
  /**
   * The maximum number of characters.
   * Displayed in the character counter.
   */
  maxLength?: number;
  onChange: (
    state: BaseTextFieldState,
    info: {
      invalid: {
        /**
         * If `true`, the value is missing, but it's required.
         */
        required: boolean;

        /**
         * The maximum allowed length that a user may enter.
         */
        maxLength: boolean;
      };
    },
  ) => void;
  validate?: (state: BaseTextFieldState) => string | undefined;
}) => {
  const getErrorMessage = useErrorMessage(maxLength);

  const uiColors = useUiColors();
  const inputCss = useTextInputStyle({
    disabled,
    icon: Boolean(icon),
  });

  const ref = useRef<HTMLInputElement>(null);
  const { isFocused, focusProps } = useFocusRing({
    isTextInput: true,
    within: true,
  });

  const parseMaxLength = () => {
    if (
      maxLength === undefined ||
      Number.isNaN(maxLength) ||
      maxLength === Number.POSITIVE_INFINITY ||
      maxLength < 0
    ) {
      return undefined;
    }
    return maxLength;
  };
  const parsedMaxLength = parseMaxLength();

  const characterCounterId = useId();

  const handleValidate = (state: BaseTextFieldState) => {
    const invalidRules = validateTextFieldValue({
      value: state.value,
      required,
      maxLength,
    });
    const customErrorMessage = validate?.(state);
    const errorMessage = Object.values(invalidRules).some(Boolean)
      ? getErrorMessage(invalidRules)
      : undefined;

    return {
      invalidRules: {
        ...invalidRules,
        custom: Boolean(customErrorMessage),
      },
      errorMessage: customErrorMessage || errorMessage,
    };
  };

  const handleChange = ({
    currentState,
  }: {
    currentState: BaseTextFieldState;
  }) => {
    const { invalidRules, errorMessage } = handleValidate(currentState);

    onChange(
      {
        ...currentState,
        errorMessage,
        errorVisible: currentState.errorVisible,
        invalid: Object.values(invalidRules).some(Boolean),
      },
      {
        invalid: {
          required: invalidRules.required,
          maxLength: invalidRules.maxLength,
        },
      },
    );
  };

  const { labelProps, inputProps, descriptionProps, errorMessageProps } =
    useTextField(
      {
        label,
        description,
        "aria-describedby": [characterCounterId, ariaDescribedBy]
          .filter(Boolean)
          .join(" "),
        type,
        isRequired: required,
        isDisabled: disabled,
        value: state.value,
        validationState:
          state.errorVisible && state.errorMessage ? "invalid" : "valid",
        autoFocus,
        inputMode,
        onChange: (value: string) => {
          handleChange({
            currentState: {
              ...state,
              value,
              errorVisible: false,
              errorMessage: state.errorMessage,
            },
          });
        },

        onBlur: () => {
          handleChange({
            currentState: {
              ...state,
              errorVisible: true,
              errorMessage: state.errorMessage,
            },
          });
        },
      },
      ref,
    );

  const isShrunk = isFocused || Boolean(state.value);

  const onClearButtonPress = () => {
    ref.current?.focus();
    handleChange({
      currentState: {
        ...state,
        value: "",
        errorVisible: false,
      },
    });
  };

  return (
    <Field
      fieldProps={focusProps}
      shrunk={isShrunk}
      focused={isFocused}
      disabled={disabled}
      stretchInputElement={true}
      icon={icon}
      label={
        <FieldLabel shrunk={isShrunk} labelProps={labelProps}>
          {label}
        </FieldLabel>
      }
      input={
        <input
          {...inputProps}
          ref={ref}
          placeholder={placeholder}
          __experimental_placeholderCss={{
            color: uiColors.textMinor,
            opacity: isFocused ? "1" : "0",
            transitionDuration: isFocused ? "300ms" : undefined,
          }}
          css={inputCss}
        />
      }
      clearButton={
        <ClearButton
          visible={Boolean(state.value) && isFocused && !disabled}
          onPress={onClearButtonPress}
        />
      }
      toolbar={toolbar}
      descriptionAndError={
        state.errorVisible && state.errorMessage ? (
          <FieldErrorMessage errorMessageProps={errorMessageProps}>
            {state.errorMessage}
          </FieldErrorMessage>
        ) : (
          description && (
            <FieldDescription descriptionProps={descriptionProps}>
              {description}
            </FieldDescription>
          )
        )
      }
      characterCounter={
        parsedMaxLength !== undefined &&
        typeof inputProps.value === "string" ? (
          <FieldCharacterCounter
            id={characterCounterId}
            maxLength={parsedMaxLength}
            length={inputProps.value.length}
          />
        ) : undefined
      }
      onClick={() => {
        ref.current?.focus();
      }}
    />
  );
};

export const createBaseTextFieldState = <T extends BaseTextFieldState>(
  defaultValue?: T,
) => {
  return {
    value: "",
    errorVisible: false,
    errorMessage: undefined,
    ...defaultValue,
  };
};

export const useBaseTextFieldState = <T extends BaseTextFieldState>(
  defaultValue?: T,
) => {
  return useState<BaseTextFieldState>(createBaseTextFieldState(defaultValue));
};

const useErrorMessage = (maxLength?: number) => {
  const message = useMessage();
  const { formatInteger } = useFormat();

  return (invalidRules: InvalidRules) => {
    if (invalidRules.maxLength && maxLength !== undefined) {
      return message({
        id: "6a80357a-9f10-43ea-9692-b5f0df038772",
        context: "Base Text field. Max length error message",
        default:
          "Maximum length: {maxLength} {maxLength, plural, one {character} few {characters} other {characters}}",
        values: { maxLength: formatInteger(maxLength) },
      });
    }
    if (invalidRules.required) {
      return message({
        id: "2f4c2f7b-4ebc-40e3-8aa5-a50548c3b457",
        context:
          "Text field. Error message in platform libraries in the Text field component.",
        default: "Required field",
      });
    }

    return undefined;
  };
};

const validateTextFieldValue = ({
  value,
  required,
  maxLength,
}: {
  value: string;
  required?: boolean;
  maxLength?: number;
}) => {
  const invalid: InvalidRules = {
    required: Boolean(required && !value),
    maxLength: false,
  };

  if (!value) {
    return invalid;
  }

  if (maxLength !== undefined) {
    invalid.maxLength = value.length > maxLength;
  }

  return invalid;
};
